import type { QuoteDocument, OptionsDocument, RatesDocument } from 'paintscout'
import clone from 'lodash/clone'
import find from 'lodash/find'
import { parseNumber, calculate as quoteCalculate } from '../../../../calculator'
import { getProductOption, getRates, type EditableRate } from '../../../options'
import type { UpdateableAreaSubstrate, UpdateableProduct } from '../../area-substrates'
import { createAreaSubstrate, updateAreaSubstrate, getAreaSubstrate } from '../../area-substrates'

export const replaceRates = ({
  quote,
  rates,
  options,
  calculate = true
}: {
  quote: Partial<QuoteDocument>
  rates: RatesDocument
  options: OptionsDocument
  calculate?: boolean
}) => {
  let updatedQuote = clone(quote) as QuoteDocument

  const allRates = getRates({ options, rates })

  Object.keys(updatedQuote.areas).forEach((areaKey) => {
    const area = updatedQuote.areas[areaKey]
    area.substrateOrder.forEach((orderItem) => {
      const areaSubstrate = getAreaSubstrate({
        quote: updatedQuote as QuoteDocument,
        options,
        areaKey,
        key: orderItem.key
      })
      const rateOption = find<EditableRate>(allRates, { name: areaSubstrate.rate?.name })
      if (rateOption) {
        const defaultProducts = getDefaultProducts({ options, rate: rateOption })
        const newAreaSubstrate: UpdateableAreaSubstrate = {
          ...createAreaSubstrate({
            rate: rateOption,
            products: defaultProducts,
            quote: updatedQuote as QuoteDocument
          }),
          quantity: Number(areaSubstrate?.quantity ?? 1),
          use_total: areaSubstrate.use_total,
          key: areaSubstrate.key
        }

        updatedQuote = updateAreaSubstrate({
          quote: updatedQuote as QuoteDocument,
          options,
          areaKey,
          areaSubstrate: newAreaSubstrate,
          calculate: false
        })
      }
    })
  })

  return calculate ? quoteCalculate(updatedQuote, options) : updatedQuote
}

function getDefaultProducts({ options, rate }: { options: OptionsDocument; rate: EditableRate }): UpdateableProduct[] {
  if (!rate?.defaultProducts && !rate?.defaultProduct) {
    return null
  }

  const productKeys = [
    ...(rate?.defaultProduct ? [rate.defaultProduct] : []),
    ...((rate?.defaultProducts ?? []).map((product) => product.productKey) ?? [])
  ]
  return productKeys.map((productKey) => {
    return getDefaultProduct({ options, rate, key: productKey })
  })
}

function getDefaultProduct({
  options,
  rate,
  key
}: {
  options: OptionsDocument
  rate: EditableRate
  key: string
}): UpdateableProduct {
  const productOption = getProductOption({ options, key })

  const { rateType } = rate
  if (productOption) {
    let coverage = productOption?.coverage_rate ?? 0
    let lnftCoverage = productOption.lnft_coverage
    let quantityCoverage = productOption.quantity_coverage

    // Use rate.defaultProducts.coverage if it exists and is a number, otherwise use rate.coverage
    // if neither exists we dont override the above values with this rateCoverage and use those instead
    const rateCoverage = (
      (rate?.defaultProducts ?? []).length
        ? () => {
            const product = rate.defaultProducts.find((product) => product.productKey === key)
            if (product && product.coverage && product.coverage !== 'null') {
              return parseNumber(product.coverage, 0)
            } else {
              return null
            }
          }
        : () => {
            if (rate.coverage && rate.coverage !== 'null') {
              return parseNumber(rate.coverage, 0)
            } else {
              return null
            }
          }
    )()

    if (typeof rateCoverage === 'number') {
      if (rateType === 'quantity') {
        quantityCoverage = rateCoverage
      } else if (rateType === 'lnft') {
        lnftCoverage = rateCoverage
      } else {
        coverage = rateCoverage
      }
    }

    return {
      ...productOption,
      color: '',
      volume: { default: 0, custom: 0, useCustom: false },
      coverage: { default: coverage, custom: 0, useCustom: false },
      lnftCoverage: { default: lnftCoverage, custom: 0, useCustom: false },
      quantityCoverage: { default: quantityCoverage, custom: 0, useCustom: false },
      price: { default: productOption.price, custom: 0, useCustom: false },
      totalPrice: { default: 0, custom: 0, useCustom: false }
    }
  }
}
